import React, { FC, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { FaArrowDown } from 'react-icons/fa';
import styled from 'styled-components';
import { bounce } from '../../../../constants/keyframes/movement';
import media from '../../../../constants/media';
import Button from '../../../base/button';
import FormikFieldWrapper from '../../../base/forms/formikFieldWrapper';
import { StyledFormikField } from '../../../base/forms/input';
import { Column, Columns } from '../../../base/grid';
import BoxImage from '../boxImage';
import { FormikFieldWithUnit } from '../../../base/forms/formikFieldWithUnit';

const Wrapper = styled.div`
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	/* Firefox */
	input[type='number'] {
		-moz-appearance: textfield;
	}
`;

const StyledButtonRow = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 1.5rem;
`;

const StyledButtonColumn = styled.div`
	width: 50%;
	padding-right: 15px;
	cursor: pointer;

	button {
		justify-content: space-between;
		padding: 0 1.8rem 0 2.2rem;
		color: ${({ theme }) => theme.colors.textDarker};
		font-family: ${({ theme }) => theme.fonts.Arimo};

		${media.mobileOnly`
			padding-left: 1.2rem;
		`};
	}

	svg {
		margin-left: 1.5rem;
		margin-right: 0rem;
		color: ${({ theme }) => theme.colors.primary};
		animation: ${bounce} 2s infinite;
	}
`;

const StyledInfoColumn = styled.div`
	width: 50%;
	padding-left: 20px;
	font-size: 1.4rem;
	color: #666;
	display: flex;
	align-items: center;
`;

interface Props {
	width: string;
	height: string;
	length: string;
	alwaysOpen?: boolean;
	infoText?: string;
}

const CalcPackageDimensions: FC<Props> = ({ height, width, length, alwaysOpen=false, infoText}) => {
	const [open, setOpen] = useState(alwaysOpen);
	const { t } = useTranslation('common');
	const onToggleOpen = () => {
		setOpen(!open);
	};

	return (
		<Wrapper>
			{!alwaysOpen &&
				<StyledButtonRow>
					<StyledButtonColumn onClick={onToggleOpen}>
						<Button type="button" style={{ width: '100%' }}>
							{t('dimension')}
							<FaArrowDown />
						</Button>
					</StyledButtonColumn>
					{infoText && (
						<StyledInfoColumn>{infoText}</StyledInfoColumn>
					)}
				</StyledButtonRow>
			}
			<AnimateHeight height={open ? 'auto' : 0}>
				<Columns>
					<Column tablet={1 / 2}>
						<FormikFieldWrapper
							marginBottom="1.5rem"
							name="parcelWidth"
							languageComponent="registerShipment">
							<FormikFieldWithUnit
								type="number"
								max="1000"
								min="0"
								id="parcelWidth"
								name="parcelWidth"
								placeholder={t('width-cm')}
								unit="cm"
							/>
						</FormikFieldWrapper>
						<FormikFieldWrapper
							marginBottom="1.5rem"
							name="parcelHeight"
							languageComponent="registerShipment">
							<FormikFieldWithUnit
								type="number"
								max="1000"
								min="0"
								id="parcelHeight"
								name="parcelHeight"
								placeholder={t('height-cm')}
								unit="cm"
							/>
						</FormikFieldWrapper>
						<FormikFieldWrapper
							marginBottom="1.5rem"
							name="parcelLength"
							languageComponent="registerShipment">
							<FormikFieldWithUnit
								type="number"
								max="1000"
								min="0"
								id="parcelLength"
								name="parcelLength"
								placeholder={t('length-cm')}
								unit="cm"
							/>
						</FormikFieldWrapper>
					</Column>
					<Column tablet={1 / 2}>
						<BoxImage width={width} height={height} length={length} />
					</Column>
				</Columns>
			</AnimateHeight>
		</Wrapper>
	);
};

export default CalcPackageDimensions;
